import { type CSSProperties, type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';
import type { DateLocaleData } from './dateLocale';
import { buildMonthGrid, todayIso } from './calendarGrid';
import { CalendarDay, type DayContext } from './CalendarDay';
import { CalendarInfinityMarkers } from './CalendarInfinityMarkers';
import { ChevronLeftIcon, ChevronRightIcon, DoubleChevronIcon } from './icons';
import type { DayNote } from '../types';

const WHEEL_COL_HEIGHT = 256;

export interface CalendarWheelsProps {
  locale: DateLocaleData;
  viewYear: number;
  viewMonth: number;
  mode: 'single' | 'range';
  from: string | null;
  to: string | null;
  hoverIso: string | null;
  hoverDir: 'fwd' | 'bwd';
  infHover: boolean;
  nights: boolean;
  openStart: boolean;
  openEnd: boolean;
  showMonthCol: boolean;
  showYearCol: boolean;
  hasDayNote: boolean;
  isDisabled: (iso: string) => boolean;
  dayTint?: (iso: string) => string | null;
  dayNote?: (iso: string) => DayNote | null;
  onSelectDay: (iso: string) => void;
  onHoverDay: (iso: string, dir: 'fwd' | 'bwd') => void;
  onGridEnter: () => void;
  onGridLeave: () => void;
  onInfEnter: (dir: 'fwd' | 'bwd') => void;
  onInfLeave: () => void;
  onClickInfinity: (dir: 'fwd' | 'bwd') => void;
  setView: (year: number, month: number) => void;
}

function navBtn(children: ReactNode, onClick: () => void, title: string) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className="text-phx-text-2 hover:bg-phx-surface-2 hover:text-phx-text inline-flex h-6 w-6 flex-none items-center justify-center rounded border-0 bg-transparent"
    >
      {children}
    </button>
  );
}

function wheelItemStyle(on: boolean): CSSProperties {
  return {
    padding: '0 6px',
    height: 20,
    fontSize: 12,
    fontWeight: on ? 600 : 400,
    background: on ? 'var(--phx-primary)' : 'transparent',
    color: on ? 'var(--phx-primary-fg)' : 'var(--phx-text-2)',
  };
}

/**
 * Calendar with "wheels" navigation (the design's only calendar mode): optional month + year
 * columns, a header with single/double arrows, and the day grid (see `CalendarDay`) with
 * range/∞/nights decoration (∞ overlay in `CalendarInfinityMarkers`).
 */
export function CalendarWheels(props: CalendarWheelsProps) {
  const { locale, viewYear, viewMonth, showMonthCol, showYearCol, hasDayNote, setView } = props;

  const cellW = hasDayNote ? 46 : 34;
  const cellH = hasDayNote ? 44 : 34;
  const cells = buildMonthGrid(viewYear, viewMonth, locale.firstDay);

  const noteFor = (iso: string): { text: string; color?: string } | null => {
    if (!props.dayNote) return null;
    const raw = props.dayNote(iso);
    if (raw == null) return null;
    return typeof raw === 'object' ? raw : { text: String(raw) };
  };

  const dayCtx: DayContext = {
    mode: props.mode,
    from: props.from,
    to: props.to,
    hoverIso: props.hoverIso,
    hoverDir: props.hoverDir,
    infHover: props.infHover,
    nights: props.nights,
    viewMonth,
    today: todayIso(),
    cellW,
    cellH,
    isDisabled: props.isDisabled,
    dayTint: props.dayTint,
    noteFor,
    onSelectDay: props.onSelectDay,
    onHoverDay: props.onHoverDay,
  };

  const setMonth = (delta: number) => {
    let m = viewMonth + delta;
    let y = viewYear;
    while (m < 0) {
      m += 12;
      y--;
    }
    while (m > 11) {
      m -= 12;
      y++;
    }
    setView(y, m);
  };

  const monthTitle = `${locale.months[viewMonth]!.charAt(0).toUpperCase()}${locale.months[viewMonth]!.slice(1)} ${viewYear}`;
  const years: number[] = [];
  for (let y = viewYear - 10; y <= viewYear + 10; y++) years.push(y);

  return (
    <div className="flex items-start gap-2">
      {showMonthCol ? (
        <div
          className="flex flex-col justify-between"
          style={{ width: 48, height: WHEEL_COL_HEIGHT }}
        >
          {locale.monthsShort.map((label, i) => (
            <div
              key={label}
              onClick={() => setView(viewYear, i)}
              className={cn(
                'flex cursor-pointer items-center justify-center rounded tabular-nums',
                i !== viewMonth && 'hover:bg-phx-surface-2'
              )}
              style={wheelItemStyle(i === viewMonth)}
            >
              {label.charAt(0).toUpperCase() + label.slice(1)}
            </div>
          ))}
        </div>
      ) : null}

      {showYearCol ? (
        <div
          className="[&::-webkit-scrollbar-thumb]:bg-phx-border-2 flex flex-col gap-px overflow-y-auto pr-0.5 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full"
          style={{ width: 52, height: WHEEL_COL_HEIGHT }}
        >
          {years.map((y) => (
            <div
              key={y}
              onClick={() => setView(y, viewMonth)}
              className={cn(
                'flex cursor-pointer items-center justify-center rounded tabular-nums',
                y !== viewYear && 'hover:bg-phx-surface-2'
              )}
              style={wheelItemStyle(y === viewYear)}
            >
              {y}
            </div>
          ))}
        </div>
      ) : null}

      <div className="flex-1">
        <div className="mb-2 flex items-center gap-0.5">
          {navBtn(
            <DoubleChevronIcon dir="left" />,
            () => setView(viewYear - 1, viewMonth),
            locale.rangeFrom
          )}
          {navBtn(<ChevronLeftIcon />, () => setMonth(-1), locale.rangeFrom)}
          <div className="text-phx-text text-phx-md flex-1 text-center font-semibold">
            {monthTitle}
          </div>
          {navBtn(<ChevronRightIcon />, () => setMonth(1), locale.rangeTo)}
          {navBtn(
            <DoubleChevronIcon dir="right" />,
            () => setView(viewYear + 1, viewMonth),
            locale.rangeTo
          )}
        </div>

        <div className="mb-0.5 grid" style={{ gridTemplateColumns: `repeat(7, ${cellW}px)` }}>
          {locale.weekdays.map((w) => (
            <div
              key={w}
              className="text-phx-text-muted text-phx-2xs py-0.5 text-center font-semibold tracking-wide uppercase"
            >
              {w}
            </div>
          ))}
        </div>

        <div className="relative" onMouseEnter={props.onGridEnter} onMouseLeave={props.onGridLeave}>
          <div className="grid" style={{ gridTemplateColumns: `repeat(7, ${cellW}px)` }}>
            {cells.map((d) => (
              <CalendarDay key={d.getTime()} date={d} ctx={dayCtx} />
            ))}
          </div>
          <CalendarInfinityMarkers
            locale={locale}
            mode={props.mode}
            from={props.from}
            to={props.to}
            hoverIso={props.hoverIso}
            hoverDir={props.hoverDir}
            infHover={props.infHover}
            openStart={props.openStart}
            openEnd={props.openEnd}
            viewYear={viewYear}
            viewMonth={viewMonth}
            cells={cells}
            cellW={cellW}
            cellH={cellH}
            onInfEnter={props.onInfEnter}
            onInfLeave={props.onInfLeave}
            onClickInfinity={props.onClickInfinity}
          />
        </div>
      </div>
    </div>
  );
}
